import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useApi } from 'api/ApiProvider';
import { ChatApiWrapper, ChatMessage, ChatSseEvent, buildChatRequest } from 'api/ChatApiWrapper';
import { AiCreditApiWrapper, UserCreditMeDTO } from 'api/AiCreditApiWrapper';
import { getApiKey } from 'components/authentication/context/authContext';

export interface SessionTokens {
  input: number;
  output: number;
}

interface ChatContextValue {
  isOpen: boolean;
  messages: ChatMessage[];
  isStreaming: boolean;
  currentReply: string;
  activeTools: string[];
  sessionTokens: SessionTokens;
  contextTokens: number;
  credit: UserCreditMeDTO | null;
  open: () => void;
  close: () => void;
  sendMessage: (text: string) => void;
  abort: () => void;
  clearConversation: () => void;
  compactConversation: () => void;
}

const ChatContext = createContext<ChatContextValue>({
  isOpen: false,
  messages: [],
  isStreaming: false,
  currentReply: '',
  activeTools: [],
  sessionTokens: { input: 0, output: 0 },
  contextTokens: 0,
  credit: null,
  open: () => {},
  close: () => {},
  sendMessage: () => {},
  abort: () => {},
  clearConversation: () => {},
  compactConversation: () => {},
});

export function useChat() {
  return useContext(ChatContext);
}

const COMPACT_PROMPT = 'Please create a concise summary of everything we\'ve discussed so far — key facts, data points, questions asked, and conclusions reached. Be brief but complete. Do not ask follow-up questions.';

export default function ChatProvider({ children }: { children: React.ReactNode }) {
  const api = useApi();

  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [currentReply, setCurrentReply] = useState('');
  const [activeTools, setActiveTools] = useState<string[]>([]);
  const [sessionTokens, setSessionTokens] = useState<SessionTokens>({ input: 0, output: 0 });
  const [contextTokens, setContextTokens] = useState(0);
  const [credit, setCredit] = useState<UserCreditMeDTO | null>(null);
  const [isCompacting, setIsCompacting] = useState(false);

  const abortControllerRef = useRef<AbortController | null>(null);

  const wrapperRef = useRef<ChatApiWrapper | null>(null);
  const creditWrapperRef = useRef<AiCreditApiWrapper | null>(null);
  if (api) {
    const baseUrl: string = (api.employeeApi as any).basePath ?? '';
    if (!wrapperRef.current) wrapperRef.current = new ChatApiWrapper(baseUrl, getApiKey);
    if (!creditWrapperRef.current) creditWrapperRef.current = new AiCreditApiWrapper(baseUrl, getApiKey);
  }

  const refreshCredit = useCallback(async () => {
    if (!creditWrapperRef.current) return;
    try { setCredit(await creditWrapperRef.current.getMe()); } catch { /* no credit configured */ }
  }, []);

  useEffect(() => { if (isOpen) refreshCredit(); }, [isOpen, refreshCredit]);

  const runStream = async (
    messagesToSend: ChatMessage[],
    onDone: (summary?: string) => void,
    signal: AbortSignal
  ) => {
    if (!wrapperRef.current) return;
    const request = buildChatRequest(messagesToSend);
    let replyText = '';

    try {
      for await (const event of wrapperRef.current.streamChat(request, signal)) {
        switch (event.type) {
          case 'text':
            replyText += event.text ?? '';
            setCurrentReply(replyText);
            break;
          case 'tool_start':
            if (event.toolName) setActiveTools(prev => [...prev, event.toolName!]);
            break;
          case 'tool_end':
            if (event.toolName) setActiveTools(prev => prev.filter(t => t !== event.toolName));
            break;
          case 'done':
            if (event.messages && !isCompacting) setMessages(event.messages);
            if (event.inputTokens != null) setContextTokens(event.inputTokens);
            if (event.inputTokens != null || event.outputTokens != null) {
              setSessionTokens(prev => ({
                input: prev.input + (event.inputTokens ?? 0),
                output: prev.output + (event.outputTokens ?? 0),
              }));
            }
            refreshCredit();
            onDone(replyText);
            break;
          case 'error':
            setMessages(prev => [...prev, { role: 'assistant', content: event.error ?? 'An error occurred.' }]);
            onDone();
            break;
        }
      }
    } catch (err: any) {
      if (err?.name !== 'AbortError') {
        setMessages(prev => [...prev, { role: 'assistant', content: 'Something went wrong. Please try again.' }]);
      }
      onDone();
    }
  };

  const sendMessage = async (text: string) => {
    if (isStreaming || !wrapperRef.current) return;

    const userMessage: ChatMessage = { role: 'user', content: text };
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setIsStreaming(true);
    setCurrentReply('');
    setActiveTools([]);

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    await runStream(updatedMessages, () => {}, abortController.signal);

    setIsStreaming(false);
    setCurrentReply('');
    setActiveTools([]);
  };

  const clearConversation = () => {
    if (isStreaming) abortControllerRef.current?.abort();
    setMessages([]);
    setSessionTokens({ input: 0, output: 0 });
    setContextTokens(0);
    setCurrentReply('');
    setActiveTools([]);
    setIsStreaming(false);
  };

  const compactConversation = async () => {
    if (isStreaming || messages.length === 0 || !wrapperRef.current) return;

    setIsCompacting(true);
    setIsStreaming(true);
    setCurrentReply('');
    setActiveTools([]);

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    const messagesToSend: ChatMessage[] = [...messages, { role: 'user', content: COMPACT_PROMPT }];

    await runStream(messagesToSend, (summary) => {
      if (summary) {
        setMessages([{ role: 'assistant', content: `**Context summary:**\n\n${summary}` }]);
        setContextTokens(0);
      }
    }, abortController.signal);

    setIsCompacting(false);
    setIsStreaming(false);
    setCurrentReply('');
    setActiveTools([]);
  };

  const abort = () => {
    abortControllerRef.current?.abort();
  };

  return (
    <ChatContext.Provider value={{
      isOpen, messages, isStreaming, currentReply, activeTools, sessionTokens, contextTokens, credit,
      open: () => setIsOpen(true),
      close: () => setIsOpen(false),
      sendMessage,
      abort,
      clearConversation,
      compactConversation,
    }}>
      {children}
    </ChatContext.Provider>
  );
}
